import React, { useMemo, useState, useEffect } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { formatCurrency, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/app/ui-bits";
import { Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Generic right-side drawer that renders ANY record cleanly.
 *
 * Props:
 *   open          — boolean, controls visibility
 *   onOpenChange  — (boolean) => void
 *   record        — the record object (any shape) OR null to hide
 *   title         — string, header (e.g. "Invoice INV-12345")
 *   subtitle      — optional string under the title (e.g. "Acme Co · $5,400")
 *   groups        — optional explicit field groups for nice rendering:
 *                   [{ label, fields: [{ key, label, format?, currency?, hide? }] }]
 *                   If omitted, the component auto-groups by heuristic.
 *   relatedTitle  — optional title for an additional "Related" block
 *   related       — optional array of `{ label, value, link? }` rows for related entities
 *   testidPrefix  — for data-testid attributes (default "record-inspector")
 */
export default function RecordInspector({
  open,
  onOpenChange,
  record,
  title,
  subtitle,
  groups,
  relatedTitle,
  related,
  testidPrefix = "record-inspector",
}) {
  // Default heuristic field-grouping if caller didn't provide one.
  const computedGroups = useMemo(() => {
    if (!record) return [];
    if (groups && groups.length) return groups;
    const keys = Object.keys(record).filter((k) => !/^_/.test(k));
    // Bucket by name
    const id = [], names = [], amounts = [], dates = [], statusKeys = [], owner = [], notes = [], other = [];
    for (const k of keys) {
      const lower = k.toLowerCase();
      const v = record[k];
      // Skip very heavy nested structures from the auto-renderer
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === "object") continue;
      if (lower === "id" || lower.endsWith("_id") || lower.endsWith("_no") || lower.endsWith("_number") || lower.endsWith("_code") || lower.endsWith("_ref") || lower.endsWith("_key")) {
        id.push(k);
      } else if (lower.includes("name") || lower === "client" || lower === "vendor" || lower === "donor" || lower === "category" || lower === "department" || lower === "designation" || lower === "currency" || lower === "bank_account" || lower === "account_no" || lower === "project_name" || lower === "payee" || lower === "spent_by" || lower === "custodian") {
        names.push(k);
      } else if (lower === "status" || lower.endsWith("_status") || lower === "report_status" || lower === "outcome") {
        statusKeys.push(k);
      } else if (lower.endsWith("_date") || lower === "date" || lower === "due" || lower === "period" || lower === "period_month" || lower.endsWith("_at") || lower.endsWith("_on")) {
        dates.push(k);
      } else if (lower.includes("amount") || lower.includes("balance") || lower.includes("total") || lower.includes("salary") || lower.includes("usd") || lower === "paid" || lower === "outstanding" || lower === "gross" || lower === "net" || lower === "additions" || lower === "deductions" || lower === "budget" || lower === "actual" || lower === "variance" || lower === "cost" || lower === "rate") {
        amounts.push(k);
      } else if (lower.includes("email") || lower.includes("by") || lower.endsWith("_owner") || lower.includes("manager")) {
        owner.push(k);
      } else if (lower.includes("note") || lower.includes("description") || lower.includes("narration") || lower.includes("comment")) {
        notes.push(k);
      } else {
        other.push(k);
      }
    }
    const mk = (label, fieldKeys) => ({ label, fields: fieldKeys.map((k) => ({ key: k })) });
    const out = [];
    if (id.length) out.push(mk("Identifiers", id));
    if (names.length) out.push(mk("Details", names));
    if (statusKeys.length) out.push(mk("Status", statusKeys));
    if (amounts.length) out.push(mk("Amounts", amounts));
    if (dates.length) out.push(mk("Dates", dates));
    if (owner.length) out.push(mk("People", owner));
    if (notes.length) out.push(mk("Notes", notes));
    if (other.length) out.push(mk("Other", other));
    return out;
  }, [record, groups]);

  if (!record) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid={`${testidPrefix}-drawer`}
      >
        <SheetHeader className="space-y-1">
          <SheetTitle className="font-display text-lg">{title || "Record details"}</SheetTitle>
          {subtitle && (
            <SheetDescription className="text-slate-600">{subtitle}</SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {computedGroups.map((g) => (
            <FieldGroup key={g.label} group={g} record={record} testidPrefix={testidPrefix} />
          ))}

          {related && related.length > 0 && (
            <div className="space-y-2 border-t border-slate-100 pt-5">
              <div className="overline text-slate-500">{relatedTitle || "Related"}</div>
              <ul className="space-y-1.5">
                {related.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-500">{r.label}</span>
                    <span className="font-medium text-slate-900 text-right">{r.value || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Embedded array fields (e.g. workflow / lines / history) */}
          {Object.entries(record).map(([k, v]) => {
            if (!Array.isArray(v) || v.length === 0 || typeof v[0] !== "object") return null;
            if (v.length > 50) return null;  // skip huge arrays — keeps drawer fast
            return <ArrayBlock key={k} label={k} rows={v} />;
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function FieldGroup({ group, record, testidPrefix }) {
  const visible = group.fields.filter((f) => {
    const v = record[f.key];
    return !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));
  });
  if (visible.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="overline text-slate-500">{group.label}</div>
      <dl className="divide-y divide-slate-100 rounded-lg border border-slate-100 bg-white">
        {visible.map((f) => (
          <FieldRow key={f.key} field={f} record={record} testidPrefix={testidPrefix} />
        ))}
      </dl>
    </div>
  );
}

function FieldRow({ field, record, testidPrefix }) {
  const v = record[field.key];
  const label = field.label || prettifyKey(field.key);
  const rendered = renderValue(field.key, v);
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm">
      <dt className="text-slate-500 shrink-0">{label}</dt>
      <dd
        className="font-medium text-slate-900 text-right max-w-[60%] break-words"
        data-testid={`${testidPrefix}-field-${field.key}`}
      >
        {rendered}
      </dd>
    </div>
  );
}

function ArrayBlock({ label, rows }) {
  const cols = useMemo(() => {
    // Pick up to 4 useful columns from the first row
    const candidates = Object.keys(rows[0] || {}).filter((k) => {
      if (k.startsWith("_")) return false;
      if (k === "id") return false;
      const v = rows[0][k];
      return v === null || ["string", "number", "boolean"].includes(typeof v);
    });
    // Prefer name/amount/status/date-like keys
    const score = (k) => {
      const lo = k.toLowerCase();
      if (lo.includes("name")) return 4;
      if (lo.includes("amount") || lo.includes("net") || lo.includes("gross")) return 3;
      if (lo.includes("status")) return 2;
      if (lo.includes("date") || lo.endsWith("_at")) return 2;
      return 1;
    };
    return candidates.sort((a, b) => score(b) - score(a)).slice(0, 4);
  }, [rows]);
  return (
    <div className="space-y-2 border-t border-slate-100 pt-5">
      <div className="overline text-slate-500">{prettifyKey(label)} <span className="text-slate-400">({rows.length})</span></div>
      <div className="responsive-table-wrap rounded-lg border border-slate-100">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              {cols.map((c) => <th key={c} className="px-2.5 py-2 text-left font-medium text-slate-600">{prettifyKey(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 12).map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {cols.map((c) => (
                  <td key={c} className="px-2.5 py-1.5 text-slate-700">
                    {renderValue(c, r[c])}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 12 && (
              <tr><td colSpan={cols.length} className="px-2.5 py-1.5 text-center text-slate-400">+ {rows.length - 12} more rows</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function prettifyKey(k) {
  return k
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bUsd\b/g, "USD")
    .replace(/\bId\b/g, "ID")
    .replace(/\bUuid\b/g, "UUID")
    .replace(/\bAr\b/g, "AR")
    .replace(/\bAp\b/g, "AP")
    .replace(/\bFx\b/g, "FX")
    .replace(/\bJv\b/g, "JV")
    .replace(/\bHr\b/g, "HR")
    .replace(/\bPc\b/g, "PC")
    .replace(/\bCeo\b/g, "CEO")
    .replace(/\bPm\b/g, "PM");
}

function isCurrencyKey(k) {
  const lo = k.toLowerCase();
  return (
    lo.includes("amount") || lo.includes("balance") || lo.includes("total") ||
    lo.includes("salary") || lo.includes("usd") || lo === "paid" ||
    lo === "outstanding" || lo === "gross" || lo === "net" ||
    lo === "additions" || lo === "deductions" || lo === "budget" ||
    lo === "budgeted_amount" || lo === "actual_amount" || lo === "variance" ||
    lo === "cost" || lo === "committed_usd" || lo === "disbursed_usd" || lo === "balance_usd" ||
    lo === "nbv" || lo === "deposits" || lo === "withdrawals" || lo === "receipts" ||
    lo === "payments" || lo === "interest_income" || lo === "bank_charges" ||
    lo === "statement_balance" || lo === "book_balance" || lo === "opening_balance" ||
    lo === "monthly_salary_usd" || lo === "depreciation_amount" || lo === "accumulated_depreciation"
  );
}
function isDateKey(k) {
  const lo = k.toLowerCase();
  return lo.endsWith("_date") || lo === "date" || lo.endsWith("_at") || lo.endsWith("_on");
}
function isStatusKey(k) {
  const lo = k.toLowerCase();
  return lo === "status" || lo.endsWith("_status") || lo === "outcome" || lo === "report_status";
}
function isPctKey(k) {
  const lo = k.toLowerCase();
  return lo.endsWith("_pct") || lo === "progress" || lo === "completion" || lo === "variance_pct";
}

function renderValue(k, v) {
  if (v === null || v === undefined || v === "") return <span className="text-slate-400">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") {
    if (Array.isArray(v)) return <span className="text-slate-400">{v.length} items</span>;
    return <CompactObject obj={v} />;
  }
  if (isStatusKey(k)) {
    return <StatusBadge status={String(v)} />;
  }
  if (isPctKey(k) && typeof v === "number") {
    return `${v}%`;
  }
  if (typeof v === "number" && isCurrencyKey(k)) {
    return <span className="font-mono-alt tabular-nums">{formatCurrency(v)}</span>;
  }
  if (typeof v === "string" && isDateKey(k)) {
    if (/^\d{4}-\d{2}$/.test(v)) return v;
    return <span className="font-mono-alt">{formatDate(v)}</span>;
  }
  if (typeof v === "string" && v.length > 200) {
    return <span className="whitespace-pre-wrap text-left block">{v}</span>;
  }
  // Long UUIDs — add a copy button
  if (typeof v === "string" && /^[0-9a-f-]{30,}$/i.test(v)) {
    return <UuidCopyable value={v} />;
  }
  return String(v);
}

function UuidCopyable({ value }) {
  return (
    <button
      type="button"
      className="font-mono-alt text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => toast.success("ID copied"));
      }}
      title="Click to copy full ID"
    >
      {value.slice(0, 8)}…<Copy size={11} />
    </button>
  );
}

function CompactObject({ obj }) {
  const entries = Object.entries(obj).filter(([k]) => !k.startsWith("_"));
  if (entries.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5 text-right">
      {entries.slice(0, 6).map(([k, v]) => (
        <div key={k} className="text-xs">
          <span className="text-slate-500">{prettifyKey(k)}: </span>
          <span className="text-slate-800">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Convenience hook — wires up open/close state + selected record.
   Usage:
     const inspector = useRecordInspector();
     ...
     <tr onClick={() => inspector.open(row, { title: row.invoice_number })}>
     ...
     <RecordInspector {...inspector.props} />
─────────────────────────────────────────────────────────────────────── */
export function useRecordInspector() {
  const [isOpen, setIsOpen] = useState(false);
  const [meta, setMeta] = useState({ record: null, title: "", subtitle: "", groups: null, related: null, relatedTitle: null });
  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => setMeta((m) => ({ ...m, record: null })), 250);
      return () => clearTimeout(t);
    }
  }, [isOpen]);
  return {
    open: (record, opts = {}) => {
      setMeta({
        record,
        title: opts.title || "Record details",
        subtitle: opts.subtitle || "",
        groups: opts.groups || null,
        related: opts.related || null,
        relatedTitle: opts.relatedTitle || null,
      });
      setIsOpen(true);
    },
    close: () => setIsOpen(false),
    props: {
      open: isOpen,
      onOpenChange: setIsOpen,
      record: meta.record,
      title: meta.title,
      subtitle: meta.subtitle,
      groups: meta.groups,
      related: meta.related,
      relatedTitle: meta.relatedTitle,
    },
  };
}
